import * as React from 'react';
import {
  getAnn,
  stripAnn,
  stripAll,
  ContextualExpression,
  ContextualExpressions,
} from '@packages/utilities/contextual-expression';
import {TypeImplementation, Types} from '@packages/systems/wfdl';
import {iconName, pluginName, qualify} from '@packages/systems/wfdl-Qualified';
import {Expression} from '@packages/systems/wfdl-ast';
import {
  ExpressionBindMetaPayload,
  ExpressionBinding,
  getBindingLabel,
} from '@packages/systems/ExpressionEditor-utils';
import {emotionStyled} from '@packages/utilities/styled';
import {
  Button,
  WarningTriangleIcon,
  ChevronSmallDownIcon,
  HStack,
} from '@packages/systems/spring';
import {useTranslation} from '@packages/systems/designer-translations';
import {SlotMetaUI} from '../slot-migration/SlotMetaUI';

const DeprecatedButton = emotionStyled(Button)({
  background: '$yellowTransparent',
  color: '$textSecondary',
  paddingLeft: '$1 !important',
  paddingRight: '$1 !important',
  gap: '$2',
  justifyContent: 'space-between',
  '&:hover': {
    background: '$yellowTransparentHover !important',
    color: '$textSecondary !important',
  },
  // Target ButtonContentView (SpaceSaver > ButtonContentView)
  '& > span > span': {
    width: '100%',
  },
});

const SpacedContent = emotionStyled(HStack)({
  alignItems: 'center',
  gap: 0,
  flex: 1,
  minWidth: 0, // Allow text truncation
});

const LabelText = emotionStyled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '$textPrimary',
  fontSize: '$body',
  marginLeft: '$0.5 !important',
});

const DeprecatedBadge = emotionStyled('span')({
  fontSize: '$caption',
  opacity: 0.8,
  marginRight: '$1 !important',
  marginLeft: '-6px !important',
});

/**
 * Render function for deprecated Devlink::Slot type
 *
 * This type is deprecated in favor of native Slots::Slot elements.
 *
 * Behavior:
 * - If no binding exists: don't render anything (field is hidden anyway in DevlinkSettings)
 * - If a binding exists: display "deprecated" ExpressionBinding UI
 */
export const SlotRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const t = useTranslation();

  const onBindingChange = React.useCallback(
    (bind: null | Expression, meta: ExpressionBindMetaPayload) => {
      // Both connecting and deleting use the same action type
      // When bind is null, it removes the binding
      dispatch({
        type: 'EXPRESSION_BIND_CHANGED',
        binding: bind,
        meta,
        key: [],
      });
    },
    [dispatch]
  );

  // Get the label from the type annotation for automation purposes
  const ann = getAnn(expr);
  const typeLabel = ann.type ? Types.getLabel(ann.type) : null;

  // We don't allow creating new DevLink Slot bindings — render nothing if unbound.
  const strippedExpr = stripAnn(expr);
  if (!ContextualExpressions.isBinding(strippedExpr)) {
    return null;
  }

  const envTypes = ann.env.context.values;
  const plainExpr = stripAll(expr);
  // Try to get the label from the binding target. If that fails, fall back to
  // the property's own label from the type annotation.
  const bindingLabel = getBindingLabel(envTypes, plainExpr);
  const propLabel = bindingLabel || typeLabel || '';

  return (
    <ExpressionBinding
      expression={expr}
      onChange={onBindingChange}
      bindUIVariant="custom"
      automationLabel={propLabel || 'Slot'}
      button={(openPopover, targetRef) => (
        <DeprecatedButton
          ref={targetRef as React.RefObject<HTMLButtonElement>}
          density="compact"
          onClick={openPopover}
          data-automation-id="deprecated-slot-binding"
        >
          <SpacedContent>
            <WarningTriangleIcon size={16} colorVariant="warning" />
            <LabelText>{propLabel}</LabelText>
          </SpacedContent>
          <SpacedContent>
            <DeprecatedBadge>
              {t('designer.components.props.devlink-slot.deprecated-badge')}
            </DeprecatedBadge>
            <ChevronSmallDownIcon size={16} />
          </SpacedContent>
        </DeprecatedButton>
      )}
    />
  );
};

export const SlotType = TypeImplementation.Custom({
  render: SlotRender,
  metaUI: SlotMetaUI,
  system: {
    bindUIVariant: 'custom',
  },
  icon: TypeImplementation.IconByName(
    qualify(pluginName('Devlink'), iconName('Slots'))
  ),
});
